import { useEffect, useRef, type ReactNode } from 'react';
import { Circle, Hexagon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { DEFAULT_GEOFENCE_COLOR, fmtArea, shapeOf, shapeSummary } from '@/lib/geofences';

import type { Geofence } from '@logistech/ui-shared';

interface Props {
  geofences: Geofence[];
  isLoading: boolean;
  selectedId: string | null;
  onPick: (id: string) => void;
  /** Contenu affiché sous l'élément sélectionné (détail, actions). */
  renderSelected?: (g: Geofence) => ReactNode;
}

export function GeofenceList({ geofences, isLoading, selectedId, onPick, renderSelected }: Props) {
  const itemRefs = useRef(new Map<string, HTMLLIElement>());

  // Sélection depuis la carte : l'élément est ramené dans la zone visible de la liste
  useEffect(() => {
    if (selectedId) itemRefs.current.get(selectedId)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  if (geofences.length === 0) {
    return <p className="text-sm text-muted-foreground py-6 text-center">Aucune zone définie, dessinez-en une</p>;
  }

  return (
    <ul className="space-y-1" aria-label="Liste des géofences">
      {geofences.map((g) => {
        const selected = g.id === selectedId;
        const Icon = shapeOf(g) === 'circle' ? Circle : Hexagon;
        return (
          <li
            key={g.id}
            ref={(el) => {
              if (el) itemRefs.current.set(g.id, el);
              else itemRefs.current.delete(g.id);
            }}
            className={cn('rounded-md border', selected ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30' : 'border-transparent')}
          >
            <button
              type="button"
              onClick={() => onPick(g.id)}
              aria-current={selected ? 'true' : undefined}
              className="w-full text-left p-2 rounded-md hover:bg-muted flex items-start gap-2"
            >
              <span
                className="mt-1 h-3 w-3 rounded-full shrink-0 border"
                style={{ backgroundColor: g.color ?? DEFAULT_GEOFENCE_COLOR }}
              />
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">{g.name}</span>
                  <Badge variant="secondary" className="shrink-0 gap-1">
                    <Icon className="h-3 w-3" aria-hidden />
                    {shapeOf(g) === 'circle' ? 'Cercle' : 'Polygone'}
                  </Badge>
                </span>
                <span className="block text-xs text-muted-foreground">
                  {shapeSummary(g).split(' · ')[1]}
                  {g.areaKm2 != null && ` · ${fmtArea(g.areaKm2)}`}
                  {` · créée le ${new Date(g.createdAt).toLocaleDateString('fr-FR')}`}
                </span>
              </span>
            </button>
            {selected && renderSelected && <div className="px-2 pb-2">{renderSelected(g)}</div>}
          </li>
        );
      })}
    </ul>
  );
}
